import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";

import { PlanDayType } from "../../DTO";
import { useAppSelector } from "../../redux-config/hooks";
import { selectWeek } from "../../redux-config/entities/week";
import { AppUserApi } from "../../api";
import { selectUser } from "../../redux-config/entities/user";
import "./style.scss";

type DayDetailProps = {
    userId: number;
    currentDay: string;
    setCurrentDay: React.Dispatch<React.SetStateAction<string>>;
    autoSelectFirstDay?: boolean;
    autoSelectCurrentDay?: boolean;
    onDaySelect?: (day: PlanDayType) => void;
    variant?: "default" | "compact";
};

const toISODate = (value: string) => value?.slice(0, 10) ?? "";

export const DayDetail: FC<DayDetailProps> = ({
    userId,
    currentDay,
    setCurrentDay,
    autoSelectFirstDay = true,
    autoSelectCurrentDay = false,
    onDaySelect,
    variant = "default",
}) => {
    const [loading, setLoading] = useState(false);
    const [days, setDays] = useState<PlanDayType[]>([]);

    const week = useAppSelector(selectWeek);
    const user = useAppSelector(selectUser);

    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

    const fetchCurrentWeekDays = useCallback(() => {
        if (!userId || !week?.index || week.index <= 0) return;

        setLoading(true);

        AppUserApi.PlanDays(user, userId, week.index)
            .then((data) => {
                const planDays: PlanDayType[] = data?.planDays ?? [];
                setDays(planDays);

                const normalizedCurrent = toISODate(currentDay);
                const currentExists = planDays.some(
                    (d) => toISODate(d.date) === normalizedCurrent,
                );

                if (planDays.length > 0 && (!normalizedCurrent || !currentExists)) {
                    if (autoSelectCurrentDay) {
                        const todayDay =
                            planDays.find((d) => d.isCurrentDay) ??
                            planDays.find((d) => toISODate(d.date) === today);
                        if (todayDay) {
                            setCurrentDay(toISODate(todayDay.date));
                            onDaySelect?.(todayDay);
                        }
                    } else if (autoSelectFirstDay) {
                        setCurrentDay(toISODate(planDays[0].date));
                    }
                }
            })
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    text: err,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [
        user,
        userId,
        week?.index,
        currentDay,
        setCurrentDay,
        autoSelectFirstDay,
    ]);

    useEffect(() => {
        fetchCurrentWeekDays();
    }, [fetchCurrentWeekDays]);

    const handleSelectDay = useCallback(
        (dayISO: string, isDisabled: boolean, day: PlanDayType) => {
            if (isDisabled) return;
            setCurrentDay(dayISO);
            onDaySelect?.(day);
        },
        [setCurrentDay, onDaySelect],
    );

    if (loading) {
        return (
            <div className="d-flex flex-row py-5 justify-content-center align-items-center">
                <Spinner animation="border" />
            </div>
        );
    }

    if (!days.length) {
        return (
            <div className="upr-empty rounded-4 border bg-white p-3 p-md-4">
                <div className="fw-semibold">No days found</div>
                <div className="text-muted small">
                    There are no plan days available for the selected week.
                </div>
            </div>
        );
    }

    return (
        <div className={`upr-days ${variant === "compact" ? "upr-days-compact" : ""}`}>
            {days.map((item) => {
                const dayISO = toISODate(item.date);
                const isFuture = dayISO > today;
                const isActive = currentDay === dayISO;
                const isToday = item.isCurrentDay && !isFuture;

                return (
                    <button
                        key={item.date}
                        type="button"
                        disabled={isFuture}
                        onClick={() => handleSelectDay(dayISO, isFuture, item)}
                        className={[
                            "upr-day-card",
                            variant === "compact" ? "upr-day-card-compact" : "",
                            "text-start",
                            "rounded-4",
                            "border",
                            "p-3",
                            "bg-white",
                            isActive ? "upr-day-active" : "",
                            isFuture ? "upr-day-disabled" : "",
                            isToday && !isActive ? "upr-day-today" : "",
                        ].join(" ")}
                    >
                        {variant === "compact" ? (
                            <>
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex align-items-center gap-1">
                                        <div className="fw-semibold upr-day-title">Day {item.name}</div>
                                        {item.isCurrentDay && (
                                            <span className="badge text-bg-primary" style={{ fontSize: "0.65rem" }}>Today</span>
                                        )}
                                    </div>
                                    <span
                                        className={[
                                            "upr-day-state-dot",
                                            isFuture
                                                ? "upr-day-state-dot-future"
                                                : isActive
                                                    ? "upr-day-state-dot-active"
                                                    : "upr-day-state-dot-available",
                                        ].join(" ")}
                                        aria-hidden="true"
                                    />
                                </div>

                                <div className="upr-day-date-row text-muted small mt-2">
                                    <span className="fw-medium text-dark">{dayISO}</span>
                                    <span
                                        className={[
                                            "upr-day-status-pill",
                                            isFuture
                                                ? "upr-day-status-pill-future"
                                                : isActive
                                                    ? "upr-day-status-pill-active"
                                                    : isToday
                                                        ? "upr-day-status-pill-available"
                                                        : "upr-day-status-pill-available",
                                        ].join(" ")}
                                    >
                                        {isFuture ? "Future" : isActive ? "Selected" : isToday ? "Today" : "Open"}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex align-items-center gap-1">
                                        <div className="fw-semibold upr-day-title">Day {item.name}</div>
                                        {item.isCurrentDay && (
                                            <span className="badge text-bg-primary" style={{ fontSize: "0.65rem" }}>Today</span>
                                        )}
                                    </div>
                                    {isFuture ? (
                                        <span className="badge text-bg-secondary upr-day-badge">
                                            Future
                                        </span>
                                    ) : isActive ? (
                                        <span className="badge text-bg-danger upr-day-badge">
                                            Selected
                                        </span>
                                    ) : (
                                        <span className="badge text-bg-light border upr-day-badge">
                                            Available
                                        </span>
                                    )}
                                </div>

                                <div className="upr-day-date-row text-muted small mt-2">
                                    <span className="me-2 upr-day-date-label">Date</span>
                                    <span className="fw-medium text-dark">{dayISO}</span>
                                </div>
                            </>
                        )}
                    </button>
                );
            })}
        </div>
    );
};
